import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Customer, Translation } from '../../../../../core/models';
import { TranslationService } from '../../../../../core/services';

export type PaymentConfirmationType =
  | 'initial_addendum'
  | 'additional_addendum'
  | 'outgoing'
  | 'dropped'
  | 'transfer'
  | 'incoming'
  | 'exchange_rate_diff';

export interface PaymentPdfTextDialogData {
  customer: Customer;
  type: PaymentConfirmationType;
}

@Component({
  selector: 'app-payment-pdf-text-dialog',
  templateUrl: './payment-pdf-text-dialog.component.html',
  styleUrls: ['./payment-pdf-text-dialog.component.scss'],
  standalone: false
})
export class PaymentPdfTextDialogComponent implements OnInit {
  selectedLanguage = 'da';
  loading = false;
  saving = false;

  form = new FormGroup({
    filename: new FormControl<string>(''),
    payment_label: new FormControl<string>(''),
    top_left: new FormControl<string>(''),
    top_right: new FormControl<string>(''),
    bottom_left: new FormControl<string>(''),
    bottom_right: new FormControl<string>('')
  });

  private typeTitles: Record<PaymentConfirmationType, string> = {
    initial_addendum: 'bevillingsbilag',
    additional_addendum: 'tillægsbevillingsbilag',
    outgoing: 'udbetalingsbilag',
    dropped: 'nedskrivningsbilag',
    transfer: 'overførselsbilag',
    incoming: 'indbetalingsbilag',
    exchange_rate_diff: 'kursdifferencebilag'
  };

  constructor(
    public dialogRef: MatDialogRef<PaymentPdfTextDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentPdfTextDialogData,
    private translationService: TranslationService
  ) {}

  get languages(): string[] {
    const langs = this.data?.customer?.settings?.board?.languages;
    return langs && langs.length ? langs : ['da'];
  }

  get title(): string {
    const name = this.typeTitles[this.data?.type] || 'bilag';
    return `Tekst på ${name} (PDF)`;
  }

  get prefix(): string {
    return `pdf.payment_confirmed_pdf.${this.data?.type}`;
  }

  ngOnInit(): void {
    if (!this.languages.includes(this.selectedLanguage)) {
      this.selectedLanguage = this.languages[0] || 'da';
    }
    this.loadTranslations();
  }

  onLanguageChange(): void {
    this.loadTranslations();
  }

  loadTranslations(): void {
    const subdomain = this.data?.customer?.subdomain || '';
    this.loading = true;

    forkJoin({
      custom: this.translationService.getTranslations(subdomain, this.selectedLanguage).pipe(
        catchError(() => of({}))
      ),
      defaults: this.translationService.getDefaultTranslations(subdomain, this.selectedLanguage).pipe(
        catchError(() => of({}))
      )
    }).subscribe({
      next: ({ custom, defaults }) => {
        this.loading = false;
        const customMap = custom instanceof Map ? Object.fromEntries(custom) : (custom || {});
        const defaultMap = defaults instanceof Map ? Object.fromEntries(defaults) : (defaults || {});

        this.form.patchValue({
          filename: customMap[`${this.prefix}.filename`] ?? defaultMap[`${this.prefix}.filename`] ?? '',
          payment_label: customMap[`${this.prefix}.payment.label`] ?? defaultMap[`${this.prefix}.payment.label`] ?? '',
          top_left: customMap[`${this.prefix}.top.left`] ?? defaultMap[`${this.prefix}.top.left`] ?? '',
          top_right: customMap[`${this.prefix}.top.right`] ?? defaultMap[`${this.prefix}.top.right`] ?? '',
          bottom_left: customMap[`${this.prefix}.bottom.left`] ?? defaultMap[`${this.prefix}.bottom.left`] ?? '',
          bottom_right: customMap[`${this.prefix}.bottom.right`] ?? defaultMap[`${this.prefix}.bottom.right`] ?? ''
        });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  doSave(): void {
    if (this.form.invalid) return;

    const subdomain = this.data?.customer?.subdomain || '';
    const translations: Translation[] = [
      { key: `${this.prefix}.filename`, value: this.form.value.filename || '' },
      { key: `${this.prefix}.payment.label`, value: this.form.value.payment_label || '' },
      { key: `${this.prefix}.top.left`, value: this.form.value.top_left || '' },
      { key: `${this.prefix}.top.right`, value: this.form.value.top_right || '' },
      { key: `${this.prefix}.bottom.left`, value: this.form.value.bottom_left || '' },
      { key: `${this.prefix}.bottom.right`, value: this.form.value.bottom_right || '' }
    ];

    this.saving = true;
    this.translationService.addNewTranslations(subdomain, this.selectedLanguage, translations).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.saving = false;
      }
    });
  }
}
